import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import { useCapability, useUpdateCapability, useDeleteCapability } from '@/hooks/useCapabilities';
import { useNeighbors } from '@/hooks/useGraph';
import { notesApi } from '@/lib/api';
import AppShell from '@/components/layout/AppShell';
import CapabilityEditor from '@/components/capabilities/CapabilityEditor';
import AIPanel from '@/components/ai/AIPanel';
import { CapabilityType, CapabilityStatus } from '@/types';
import type { Capability } from '@/types';

// ─── Strength ring ────────────────────────────────────────────────────────────

const StrengthRing: React.FC<{ value: number; size?: number }> = ({ value, size = 48 }) => {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (value / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-surface-200 dark:text-surface-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          className="text-accent-500 transition-all duration-700"
        />
      </svg>
      <span className="absolute text-xs font-bold text-surface-700 dark:text-surface-300">
        {value}
      </span>
    </div>
  );
};

// ─── Type + status selectors ──────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  skill: 'Skill',
  knowledge: 'Knowledge',
  experience: 'Experience',
  tool: 'Tool',
  relationship: 'Relationship',
  credential: 'Credential',
  domain: 'Domain',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  growing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  dormant: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  deprecated: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

// ─── CapabilityPage ───────────────────────────────────────────────────────────

interface RouteParams {
  id: string;
}

const CapabilityPage: React.FC = () => {
  const { id: capabilityId } = useParams<RouteParams>();
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;

  const activePanel = useUIStore((s) => s.activePanel);
  const setActivePanel = useUIStore((s) => s.setActivePanel);

  const { data: capability, isLoading, error } = useCapability(workspaceId, capabilityId);
  const updateMutation = useUpdateCapability(workspaceId ?? '');
  const deleteMutation = useDeleteCapability(workspaceId ?? '');

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editStrength, setEditStrength] = useState(0);
  const [editType, setEditType] = useState<string>('skill');
  const [editStatus, setEditStatus] = useState<string>('active');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (capability) {
      setEditTitle(capability.title);
      setEditSummary(capability.summary ?? '');
      setEditStrength(capability.strength);
      setEditType(capability.type);
      setEditStatus(capability.status);
    }
  }, [capability]);

  // Find the node id for this capability (for graph neighbors)
  const nodeId = Object.values(useWorkspaceStore.getState().nodes).find(
    (n) => n.capabilityId === capabilityId
  )?.id ?? null;

  const { incoming, outgoing } = useNeighbors(nodeId);
  const capabilities = useWorkspaceStore((s) => s.capabilities);

  const { data: linkedNotes } = useQuery({
    queryKey: ['notes-for-capability', workspaceId, capabilityId],
    queryFn: async () => {
      if (!workspaceId) return [];
      return notesApi.listForCapability(workspaceId, capabilityId);
    },
    enabled: Boolean(workspaceId && capabilityId),
    staleTime: 30_000,
  });

  const handleSave = useCallback(async () => {
    if (!workspaceId || !capabilityId) {
      return;
    }
    await updateMutation.mutateAsync({
      id: capabilityId,
      data: {
        title: editTitle,
        summary: editSummary || null,
        strength: editStrength,
        type: editType as Capability['type'],
        status: editStatus as Capability['status'],
      },
    });
    setIsEditing(false);
  }, [workspaceId, capabilityId, editTitle, editSummary, editStrength, editType, editStatus, updateMutation]);

  const handleContentChange = useCallback(
    async (content: string) => {
      if (!workspaceId || !capabilityId) {
        return;
      }
      await updateMutation.mutateAsync({ id: capabilityId, data: { content } });
    },
    [workspaceId, capabilityId, updateMutation]
  );

  const handleDelete = useCallback(async () => {
    if (!workspaceId || !capabilityId) {
      return;
    }
    await deleteMutation.mutateAsync(capabilityId);
    history.replace('/capabilities');
  }, [workspaceId, capabilityId, deleteMutation, history]);

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full">
          <svg className="animate-spin w-8 h-8 text-primary-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      </AppShell>
    );
  }

  if (error || !capability) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-lg font-semibold text-surface-700 dark:text-surface-300">
              Capability not found
            </p>
            <button
              type="button"
              onClick={() => history.replace('/capabilities')}
              className="mt-4 text-sm text-primary-600 hover:underline"
            >
              Back to capabilities
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const linkedCapabilities = [
    ...outgoing.map((n) => ({ node: n, direction: 'outgoing' as const })),
    ...incoming.map((n) => ({ node: n, direction: 'incoming' as const })),
  ];

  return (
    <AppShell>
      <div className="flex h-full overflow-hidden">
        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-6">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm text-surface-400">
                  <button
                    type="button"
                    onClick={() => history.push('/capabilities')}
                    className="hover:text-primary-600 transition-colors"
                  >
                    Capabilities
                  </button>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                  <span className="text-surface-600 dark:text-surface-400 truncate max-w-xs">
                    {capability.title}
                  </span>
                </nav>

                {/* Header */}
                <div className="flex items-start gap-4">
                  <StrengthRing value={capability.strength} size={56} />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full text-2xl font-bold bg-transparent border-b-2 border-primary-500 outline-none text-surface-900 dark:text-white pb-1"
                        autoFocus
                      />
                    ) : (
                      <h1 className="text-2xl font-bold text-surface-900 dark:text-white">
                        {capability.title}
                      </h1>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[capability.status] ?? ''}`}>
                        {capability.status}
                      </span>
                      <span className="text-xs text-surface-500">
                        {TYPE_LABELS[capability.type] ?? capability.type}
                      </span>
                      <span className="text-2xs text-surface-400">
                        Updated {formatDistanceToNow(new Date(capability.updatedAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-3 py-1.5 text-xs text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-60"
                        >
                          {updateMutation.isPending ? 'Saving…' : 'Save'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setActivePanel(activePanel === 'ai-suggestions' ? 'none' : 'ai-suggestions')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M12 2a10 10 0 1 0 10 10" /><path d="M22 2 12 12" /><path d="m17 2 5 5-5 5" />
                          </svg>
                          AI
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditing(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors border border-surface-200 dark:border-surface-700"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Inline edit fields */}
                {isEditing && (
                  <div className="bg-surface-50 dark:bg-surface-800/50 rounded-xl p-4 space-y-4 border border-surface-200 dark:border-surface-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1">Type</label>
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value)}
                          className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2 outline-none focus:border-primary-500"
                        >
                          {Object.values(CapabilityType).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1">Status</label>
                        <select
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                          className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2 outline-none focus:border-primary-500"
                        >
                          {Object.values(CapabilityStatus).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1">
                        Strength: {editStrength}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={editStrength}
                        onChange={(e) => setEditStrength(parseInt(e.target.value, 10))}
                        className="w-full accent-accent-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1">Summary</label>
                      <textarea
                        value={editSummary}
                        onChange={(e) => setEditSummary(e.target.value)}
                        rows={2}
                        placeholder="Brief description…"
                        className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2 outline-none focus:border-primary-500 resize-none"
                      />
                    </div>
                  </div>
                )}

                {/* Summary */}
                {!isEditing && capability.summary && (
                  <p className="text-sm text-surface-600 dark:text-surface-400 leading-relaxed">
                    {capability.summary}
                  </p>
                )}

                {/* Tags */}
                {capability.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {capability.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-surface-100 dark:bg-surface-800 text-xs text-surface-600 dark:text-surface-400"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* TipTap editor */}
                <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-card overflow-hidden">
                  <CapabilityEditor
                    content={capability.content}
                    onUpdate={handleContentChange}
                    editable={!isEditing}
                  />
                </div>

                {/* Evidence */}
                {capability.evidence.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-3">
                      Evidence & proof points
                    </h3>
                    <div className="space-y-2">
                      {capability.evidence.map((ev) => (
                        <div
                          key={ev.id}
                          className="flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700"
                        >
                          <span className="text-xs font-semibold text-surface-500 uppercase flex-shrink-0 w-16 text-right">{ev.type}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-surface-700 dark:text-surface-300 truncate">
                              {ev.label}
                            </p>
                            {ev.date && (
                              <p className="text-xs text-surface-400">{ev.date}</p>
                            )}
                          </div>
                          {ev.url && (
                            <a
                              href={ev.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked capabilities */}
                {linkedCapabilities.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-3">
                      Linked capabilities
                    </h3>
                    <div className="space-y-2">
                      {linkedCapabilities.map(({ node, direction }) => {
                        const linkedCap = capabilities[node.capabilityId];
                        if (!linkedCap) {
                          return null;
                        }
                        return (
                          <button
                            key={node.id}
                            type="button"
                            onClick={() => history.push(`/capabilities/${linkedCap.id}`)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700 hover:border-primary-300 dark:hover:border-primary-700 transition-colors text-left"
                          >
                            <span className="text-surface-400 text-xs">
                              {direction === 'outgoing' ? '→' : '←'}
                            </span>
                            <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                              {linkedCap.title}
                            </span>
                            <span className="ml-auto text-xs text-surface-400">{linkedCap.type}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Linked notes */}
                {linkedNotes && linkedNotes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3" style={{
                      fontFamily: 'var(--font-display)',
                      color: 'var(--text)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}>
                      Notes
                    </h3>
                    <div className="space-y-2">
                      {linkedNotes.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => history.push(`/notes?note=${n.id}`)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                          style={{
                            background: 'var(--bg-mid)',
                            border: '1px solid var(--border)',
                          }}
                          onMouseOver={(e) => {
                            (e.currentTarget as HTMLElement).style.borderColor = 'var(--crimson-border)';
                          }}
                          onMouseOut={(e) => {
                            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                          }}
                        >
                          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" style={{ color: 'var(--text-muted)' }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                            {n.title.trim() || 'Untitled'}
                          </span>
                          <span className="ml-auto text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                            {formatDistanceToNow(new Date(n.updatedAt), { addSuffix: true })}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Danger zone */}
                <div className="border-t border-surface-200 dark:border-surface-800 pt-6">
                  {!confirmDelete ? (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="text-xs text-red-500 hover:text-red-600 hover:underline"
                    >
                      Delete this capability
                    </button>
                  ) : (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-3">
                        Are you sure? This cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={deleteMutation.isPending}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-60"
                        >
                          {deleteMutation.isPending ? 'Deleting…' : 'Yes, delete'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(false)}
                          className="px-3 py-1.5 text-xs text-surface-600 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* AI Panel */}
            {activePanel === 'ai-suggestions' && workspaceId && (
              <AIPanel
                workspaceId={workspaceId}
                capabilityId={capabilityId}
                onClose={() => setActivePanel('none')}
              />
            )}
      </div>
    </AppShell>
  );
};

export default CapabilityPage;
